import { AUTH_API_URL } from '#config/runtimeconfig';
import Logger from './Logger';
import { fetchAuthSession } from 'aws-amplify/auth';
import fromState from '../state/selectors';
import store from '/b2b/store';

/*
 * Token broker for chat-api. The only place in the app that knows how a chat-api
 * credential is obtained: the authentication-service mints a short-lived RS256 JWT
 * (aud=chat-api, 5-minute TTL) from the live Cognito session, and chat-api verifies
 * it offline. See "DACI — Platform Browser-Direct Authentication" and
 * "authentication-service — Chat-API Browser Token Mint" in Confluence.
 *
 * The token is cached per customer: a cache entry for a different customer is a miss,
 * so switching the active customer always mints a fresh tenant-scoped token even
 * before clearChatToken() runs.
 */

const MINT_PATH = '/auth/token';
const TOKEN_AUDIENCE = 'chat-api';

/*
 * Re-mint this far ahead of expiry rather than on a timer: every chat action goes
 * through here, and an SSE turn is authorized when the stream opens, so a token
 * expiring mid-response never interrupts it.
 */
const REFRESH_MARGIN_MS = 60_000;
// Used only when the token's own exp claim can't be read; the documented TTL is 300s.
const FALLBACK_TTL_MS = 240_000;

/** @type {{customerId: string, token: string, expiresAt: number}|null} */
let cached = null;
/** @type {{customerId: string, promise: Promise<string>}|null} */
let inFlight = null;

/**
 * Errors carry the mint's HTTP status so callers can map it onto their own error
 * vocabulary (a 403 means "authenticated but holds no osano-ai scopes", which is a
 * different story from a 401).
 * @param {string} message
 * @param {number} [status]
 */
const authError = (message, status) =>
    Object.assign(new Error(message), { name: 'ChatAuthError', status });

/**
 * Reads the exp claim without verifying the signature — the browser is not the
 * verifier, it only needs to know when to ask for a new token.
 * @param {string} token
 * @returns {number|null} Expiry in epoch ms, or null when the payload can't be read.
 */
const readExpiry = token => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const { exp } = JSON.parse(window.atob(padded));
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
};

const getActiveCustomerId = () => fromState.Auth.getCustomerId(store.getState());

/**
 * The live Cognito ID token, from Redux when it's there and from Amplify otherwise —
 * the same fallback chain the Amplify REST header builder uses.
 * @returns {Promise<string>}
 */
const getSessionToken = async () => {
    const session = fromState.Auth.getSessionToken(store.getState());
    if (session) return session;
    const { tokens: { idToken = '' } = {} } = await fetchAuthSession();
    return idToken.toString();
};

/**
 * @param {string} customerId
 * @returns {Promise<{customerId: string, token: string, expiresAt: number}>}
 */
const mintToken = async customerId => {
    const session = await getSessionToken();
    if (!session) {
        throw authError('No active session to mint a chat token from', 401);
    }

    let response;
    try {
        response = await fetch(`${AUTH_API_URL}${MINT_PATH}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-osno-auth': session,
            },
            // customerId is caller-supplied but verified server-side against the
            // authenticated user's membership before it enters the token.
            body: JSON.stringify({ customerId, audience: TOKEN_AUDIENCE }),
        });
    } catch (error) {
        Logger.error('Chat token mint failed to reach the authentication service:', error.message);
        throw authError('Could not reach the authentication service');
    }

    if (!response.ok) {
        // The body may carry a message but never anything we should log verbatim.
        Logger.error(`Chat token mint rejected with status ${response.status}`);
        throw authError(`Chat token mint failed with status ${response.status}`, response.status);
    }

    const { token } = (await response.json()) || {};
    if (!token) {
        throw authError('Chat token mint returned no token');
    }

    return { customerId, token, expiresAt: readExpiry(token) ?? Date.now() + FALLBACK_TTL_MS };
};

/**
 * A cached, unexpired chat-api JWT for the active customer, minting one when needed.
 * Concurrent callers share a single mint.
 *
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh] Discard the cached token first — used once
 *   after a 401 from chat-api, so an early-revoked or clock-skewed token self-heals.
 * @returns {Promise<string>}
 */
export const getChatToken = async ({ forceRefresh = false } = {}) => {
    const customerId = getActiveCustomerId();

    if (forceRefresh) {
        // Drop the cached token and disown any mint already running against it.
        cached = null;
        inFlight = null;
    }

    if (
        cached &&
        cached.customerId === customerId &&
        cached.expiresAt - REFRESH_MARGIN_MS > Date.now()
    ) {
        return cached.token;
    }

    if (!inFlight || inFlight.customerId !== customerId) {
        const entry = { customerId, promise: null };
        entry.promise = mintToken(customerId)
            .then(minted => {
                cached = minted;
                return minted.token;
            })
            .finally(() => {
                // Identity, not customerId: a forceRefresh may have replaced this entry.
                if (inFlight === entry) inFlight = null;
            });
        inFlight = entry;
    }

    return inFlight.promise;
};

/**
 * Forget the cached token. Called on customer switch and on assistant reset so a
 * stale tenant-scoped credential can never outlive the context it was minted for.
 */
export const clearChatToken = () => {
    cached = null;
    inFlight = null;
};
